import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

// Admin pages: maintain holidays and projects.

function dateLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });
}

// Runs an API call, then shows its message (or error) and reloads the list.
function useAdminAction(reload) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const run = async (call) => {
    setBusy(true);
    setNotice("");
    setError("");
    try {
      const res = await call();
      setNotice(res.message);
      reload();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, notice, error, run };
}

const Messages = ({ notice, error }) => (
  <>
    {notice && <div className="timesheet-notice">{notice}</div>}
    {error && <div className="timesheet-error">{error}</div>}
  </>
);

export function Holidays({ userDetails }) {
  const email = userDetails.EmpEmail;
  const [holidays, setHolidays] = useState([]);
  const [form, setForm] = useState({ date: "", name: "" });
  const [editing, setEditing] = useState(null); // { id, date, name }
  const [loadError, setLoadError] = useState("");

  const load = useCallback(() => {
    api.getHolidays().then(setHolidays).catch((e) => setLoadError(e.message));
  }, []);

  const { busy, notice, error, run } = useAdminAction(load);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (await run(() => api.addHoliday(email, form.date, form.name.trim()))) setForm({ date: "", name: "" });
  };

  const save = async () => {
    if (await run(() => api.updateHoliday(email, editing.id, editing.date, editing.name.trim()))) setEditing(null);
  };

  const remove = (h) => {
    if (window.confirm(`Delete the holiday "${h.name}" on ${dateLabel(h.date)}?`)) run(() => api.deleteHoliday(email, h.id));
  };

  return (
    <section className="timesheet-container">
      <div className="timesheet-head">
        <h2>Holidays</h2>
      </div>

      <form className="timesheet-entry-box" onSubmit={add}>
        <div className="timesheet-entry-title">New holiday</div>
        <div className="timesheet-entry-fields">
          <label className="timesheet-field timesheet-field-date">
            <span>Date</span>
            <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </label>
          <label className="timesheet-field timesheet-field-desc">
            <span>Holiday name</span>
            <input
              required maxLength={120} placeholder="e.g. Diwali" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <div className="timesheet-field timesheet-field-action">
            <span aria-hidden="true">&nbsp;</span>
            <button className="timesheet-primary" type="submit" disabled={busy}>Add holiday</button>
          </div>
        </div>
      </form>

      <Messages notice={notice} error={error || loadError} />

      <div className="timesheet-table-wrap">
        <table className="timesheet-table">
          <thead>
            <tr>
              <th className="timesheet-col-index">#</th>
              <th>Date</th>
              <th>Holiday</th>
              <th className="timesheet-col-admin" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {holidays.length === 0 ? (
              <tr><td colSpan={4} className="timesheet-table-empty">No holidays added yet.</td></tr>
            ) : (
              holidays.map((h, i) =>
                editing?.id === h.id ? (
                  <tr key={h.id}>
                    <td className="timesheet-col-index">{i + 1}</td>
                    <td>
                      <input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
                    </td>
                    <td>
                      <input
                        className="timesheet-inline-input" maxLength={120} value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      />
                    </td>
                    <td className="timesheet-col-admin">
                      <button className="timesheet-link" onClick={save} disabled={busy || !editing.date || !editing.name.trim()}>Save</button>
                      <button className="timesheet-link" onClick={() => setEditing(null)}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={h.id}>
                    <td className="timesheet-col-index">{i + 1}</td>
                    <td className="timesheet-nowrap">{dateLabel(h.date)}</td>
                    <td><span className="timesheet-project">{h.name}</span></td>
                    <td className="timesheet-col-admin">
                      <button className="timesheet-link" onClick={() => setEditing({ ...h })}>Edit</button>
                      <button className="timesheet-link danger" onClick={() => remove(h)} disabled={busy}>Delete</button>
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function Projects({ userDetails, projects, onChange }) {
  const email = userDetails.EmpEmail;
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null); // { id, name }
  const { busy, notice, error, run } = useAdminAction(onChange);

  const add = async (e) => {
    e.preventDefault();
    if (await run(() => api.addProject(email, name.trim()))) setName("");
  };

  const save = async () => {
    if (await run(() => api.updateProject(email, editing.id, editing.name.trim()))) setEditing(null);
  };

  const remove = (p) => {
    if (window.confirm(`Delete the project "${p.name}"? Existing time entries keep it, but it can no longer be picked.`)) {
      run(() => api.deleteProject(email, p.id));
    }
  };

  return (
    <section className="timesheet-container">
      <div className="timesheet-head">
        <h2>Projects</h2>
      </div>

      <form className="timesheet-entry-box" onSubmit={add}>
        <div className="timesheet-entry-title">New project</div>
        <div className="timesheet-entry-fields">
          <label className="timesheet-field timesheet-field-desc">
            <span>Project name</span>
            <input required placeholder="Project name" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <div className="timesheet-field timesheet-field-action">
            <span aria-hidden="true">&nbsp;</span>
            <button className="timesheet-primary" type="submit" disabled={busy}>Add project</button>
          </div>
        </div>
      </form>

      <Messages notice={notice} error={error} />

      <div className="timesheet-table-wrap">
        <table className="timesheet-table">
          <thead>
            <tr>
              <th className="timesheet-col-index">#</th>
              <th>Project</th>
              <th className="timesheet-col-admin" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 ? (
              <tr><td colSpan={3} className="timesheet-table-empty">No projects added yet.</td></tr>
            ) : (
              projects.map((p, i) =>
                editing?.id === p.id ? (
                  <tr key={p.id}>
                    <td className="timesheet-col-index">{i + 1}</td>
                    <td>
                      <input
                        className="timesheet-inline-input" value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      />
                    </td>
                    <td className="timesheet-col-admin">
                      <button className="timesheet-link" onClick={save} disabled={busy || !editing.name.trim()}>Save</button>
                      <button className="timesheet-link" onClick={() => setEditing(null)}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={p.id}>
                    <td className="timesheet-col-index">{i + 1}</td>
                    <td><span className="timesheet-project">{p.name}</span></td>
                    <td className="timesheet-col-admin">
                      <button className="timesheet-link" onClick={() => setEditing({ ...p })}>Edit</button>
                      <button className="timesheet-link danger" onClick={() => remove(p)} disabled={busy}>Delete</button>
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
